import { useMediaQuery } from '@mui/material';
import { differenceInMinutes } from 'date-fns';
import { useCallback } from 'react';
import { TrackableEventName, TrackableEventType } from '../constants';
import { Profile } from '../global-state/types';
import { muiTheme } from '../theme';
import { RoleMapping, SuamRole } from '../utils';
import { useUser } from './useController';
import { useStoreInformation } from './useStoreInformation';

const SESSION_INACTIVITY_DURATION_MINUTES = 30;

export const trackingSessionIdKey = 'trackingSessionId';
export const lastTrackingEventTimeKey = 'lastTrackingEventTime';

export type Parameter = {
  name: string;
  stringValue: string;
};

type EventPayload = {
  eventName: string;
  eventType: string;
  parameters: Parameter[];
  env: string;
  sId: string;
  source: string;
  brandId?: number;
};

const generateSessionId = (): string => {
  const randomArray = new Uint8Array(16); // 16 bytes = 128 bits of randomness
  window.crypto.getRandomValues(randomArray);

  const randomString = Array.from(randomArray, (byte) => byte.toString(16).padStart(2, '0')).join('');
  const timestamp = Date.now();
  return `${randomString}:${timestamp}`;
};

const getSessionId = (): string => {
  try {
    const currentTrackingSessionId = sessionStorage.getItem(trackingSessionIdKey);

    if (!currentTrackingSessionId) {
      const newTrackingSessionId = generateSessionId();
      const newLastTrackingEventTime = Date.now().toString();

      sessionStorage.setItem(trackingSessionIdKey, newTrackingSessionId);
      sessionStorage.setItem(lastTrackingEventTimeKey, newLastTrackingEventTime);

      return newTrackingSessionId;
    }

    const lastTrackingEventTime = sessionStorage.getItem(lastTrackingEventTimeKey);

    if (!lastTrackingEventTime) {
      sessionStorage.setItem(lastTrackingEventTimeKey, Date.now().toString());

      return currentTrackingSessionId;
    }

    const minutesSinceLastEvent = differenceInMinutes(Date.now(), Number.parseInt(lastTrackingEventTime));

    if (minutesSinceLastEvent < SESSION_INACTIVITY_DURATION_MINUTES) {
      sessionStorage.setItem(lastTrackingEventTimeKey, Date.now().toString());
      return currentTrackingSessionId;
    }

    const newTrackingSessionId = generateSessionId();
    const newLastTrackingEventTime = Date.now().toString();

    sessionStorage.setItem(trackingSessionIdKey, newTrackingSessionId);
    sessionStorage.setItem(lastTrackingEventTimeKey, newLastTrackingEventTime);

    return newTrackingSessionId;
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
  } catch (_err) {
    const newTrackingSessionId = generateSessionId();
    const newLastTrackingEventTime = Date.now().toString();

    sessionStorage.setItem(trackingSessionIdKey, newTrackingSessionId);
    sessionStorage.setItem(lastTrackingEventTimeKey, newLastTrackingEventTime);

    return newTrackingSessionId;
  }
};

const getRole = (profile: Profile) => {
  if (!profile.assignments || profile.assignments.length === 0) return '';

  const suamRoleCode = profile.assignments[0].suamRoleCode;
  return suamRoleCode ? RoleMapping[suamRoleCode as SuamRole] : profile.assignments[0].title || '';
};

const getApiUrl = (): string => {
  const env = process.env.REACT_APP_ENV;
  switch (env) {
    case 'production':
      return 'https://depcollect.hmgroup.com/dep/v1/internal/';
    case 'test':
      return 'https://test-depcollect.hmgroup.com/dep/v1/internal/';
    default:
      return 'https://dev-depcollect.hmgroup.com/dep/v1/internal/';
  }
};

const useEventTracker = () => {
  const isTablet = useMediaQuery(muiTheme.breakpoints.down('tabletLandscape'));
  const isMobile = useMediaQuery(muiTheme.breakpoints.down('tabletPortrait'));

  const API_URL = getApiUrl();
  const {
    get: { currentStoreId, profile },
  } = useUser();
  const { countryCode, corporateBrandId } = useStoreInformation();

  const trackEvent = useCallback(
    async (
      eventName: TrackableEventName,
      eventType: TrackableEventType,
      parameters: Parameter[],
    ): Promise<void> => {
      const sessionId = getSessionId();

      const storeParameters = {
        storeId: currentStoreId,
        countryCode,
        brandId: corporateBrandId,
        userRole: getRole(profile),
      };

      const payload: EventPayload = {
        eventName,
        eventType,
        parameters: [
          { name: 'store_id', stringValue: storeParameters.storeId },
          { name: 'country_code', stringValue: storeParameters.countryCode },
          { name: 'brand_id', stringValue: storeParameters.brandId.toString() },
          { name: 'user_role', stringValue: storeParameters.userRole },
          { name: 'Touchpoint', stringValue: isMobile ? 'mobile' : isTablet ? 'tablet' : 'desktop' },
          ...parameters,
        ],
        env: process.env.REACT_APP_ENV || 'development',
        sId: sessionId,
        source: 'box',
      };

      try {
        const success = navigator.sendBeacon(API_URL, JSON.stringify(payload));
        if (!success) {
          throw new Error('sendBeacon failed to send the request.');
        }
      } catch (error) {
        console.error('Error tracking event:', (error as Error).message);
      }
    },
    [API_URL, currentStoreId, countryCode, corporateBrandId, profile, isMobile, isTablet],
  );

  return trackEvent;
};

export default useEventTracker;
